import { z } from 'zod';
import { CalendarDateSchema } from './common.js';
// =============================================================================
// Bank Transfer
//
// Pairs two bank statement lines (a money-out line on the source bank
// account, a money-in line on the destination bank account) as a single
// inter-account transfer. See apps/backend/platform/src/domains/bank-transfer
// for the full lifecycle. Both routes are MCP-disabled - a transfer is a
// ledger mutation that requires human intent, mirroring the disabled
// allocate/unallocate bank-statement-line mutations. (Voiding a transfer is
// deferred to a follow-up.)
// =============================================================================
export const BankTransferStatusSchema = z.enum(['PENDING', 'COMPLETE']);
export const BankTransferSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    ledgerId: z.string().uuid(),
    currency: z.string(),
    /** Positive magnitude - the transfer amount, derived from whichever line(s) are linked. */
    amountMinorUnits: z.number().int(),
    sourceBankAccountId: z.string().uuid(),
    destBankAccountId: z.string().uuid(),
    /**
     * Set only once the source leg's entry set has posted. PENDING = exactly
     * one of sourceLineId/destLineId is set; COMPLETE = both.
     */
    sourceLineId: z.string().uuid().nullable(),
    /** Set only once the destination leg's entry set has posted. See sourceLineId. */
    destLineId: z.string().uuid().nullable(),
    status: BankTransferStatusSchema,
    /** ISO 8601 calendar date (YYYY-MM-DD) - the accountingDate both legs post under. */
    date: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Request body for POST /accounts/:accountId/bank-transfers.
 *
 * Creates a bank transfer pairing intent and posts whichever leg(s) are
 * supplied. At least one of `sourceLineId` / `destLineId` is required - a
 * transfer created with only one line lands PENDING (a one-legged transfer
 * awaiting its counterpart, completed later via attach-line); supplying both
 * posts both legs immediately and returns COMPLETE.
 *
 * - `sourceLineId`: the money-out bank statement line on `sourceBankAccountId`.
 *   Optional; omit when the destination leg has not arrived on the statement yet.
 * - `destLineId`: the money-in bank statement line on `destBankAccountId`.
 *   Optional; omit when the source leg has not arrived on the statement yet.
 * - `sourceBankAccountId` / `destBankAccountId`: must differ and resolve to a
 *   BANK ledger account on the SAME ledger.
 *
 * `amountMinorUnits` and `currency` are NOT caller inputs - they are derived
 * server-side from whichever line(s) are supplied.
 */
export const CreateBankTransferInputSchema = z.object({
    sourceBankAccountId: z.string().uuid(),
    destBankAccountId: z.string().uuid(),
    sourceLineId: z.string().uuid().optional(),
    destLineId: z.string().uuid().optional(),
    date: CalendarDateSchema,
    /**
     * Caller-supplied idempotency key. A retry with the same key against the
     * same account never inserts a second transfer row. Use a UUID generated
     * once per transfer intent and keep it stable across retries.
     */
    idempotencyKey: z.string().min(1).max(255),
});
/**
 * Request body for POST /accounts/:accountId/bank-transfers/:id/attach-line.
 *
 * Fills the null side of a PENDING bank transfer with a newly supplied line,
 * completing it (status becomes COMPLETE). The line must belong to whichever
 * bank account the still-open side of the transfer references, carry the
 * correct sign for that side, and match the transfer's amount and currency.
 * Calling this on an already-COMPLETE transfer with the exact line that
 * completed it is a no-op replay, not an error.
 */
export const AttachLineToBankTransferInputSchema = z.object({
    lineId: z.string().uuid(),
    /** Caller-supplied idempotency key. A retry with the same key is a no-op. */
    idempotencyKey: z.string().min(1).max(255),
});
// =============================================================================
// Bank Transfer - reconcile reads
//
// Two line-keyed reads that feed the bank-statement reconcile UX. Both enrich
// the raw transfer with the label of the counterpart bank account (resolved
// server-side) so the client renders "Transfer from/to <account>" without
// holding a bank-account map.
// =============================================================================
/**
 * Response row for GET
 * /accounts/:accountId/bank-statement-lines/:lineId/completable-transfers.
 *
 * A PENDING transfer the line could complete: same magnitude, same bank
 * account, open leg on the side the line's sign dictates.
 * `counterpartBankAccountLabel` is the already-posted (other) leg's bank account.
 */
export const CompletableTransferSchema = z.object({
    id: z.string().uuid(),
    /** Positive magnitude, equal to the line's absolute amount. */
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 calendar date the transfer posts under. */
    date: z.string(),
    counterpartBankAccountId: z.string().uuid(),
    counterpartBankAccountLabel: z.string(),
});
export const ListCompletableTransfersResponseSchema = z.object({
    data: z.array(CompletableTransferSchema),
});
/**
 * Response row for GET
 * /accounts/:accountId/bank-transfers/completable-open-sides.
 *
 * One row in the MCP-disabled, complete-or-error account snapshot of attachable
 * one-legged PENDING transfers. Unlike the line-keyed picker above, this read
 * carries no bank-account labels: it is the bounded (maximum 1,000), PII-free
 * discovery input used by the bookkeeper candidate report.
 */
export const CompletableTransferOpenSideSchema = z.object({
    transferId: z.string().uuid(),
    openBankAccountId: z.string().uuid(),
    openSide: z.enum(['source', 'dest']),
    /** Positive transfer magnitude. */
    amountMinorUnits: z.number().int().min(1),
    currency: z.string().min(1),
    /** RFC 3339 datetime. */
    date: z.iso.datetime(),
    counterpartBankAccountId: z.string().uuid(),
});
export const ListCompletableTransferOpenSidesResponseSchema = z.object({
    data: z.array(CompletableTransferOpenSideSchema),
});
/**
 * Response for GET /accounts/:accountId/bank-statement-lines/:lineId/transfer.
 *
 * The transfer a reconciled bank statement line belongs to (as its source or
 * dest leg), or null when the line is not a transfer leg. `thisLineSide` is
 * the side the queried line occupies; `counterpartBankAccountLabel` names the
 * other end. Used for the reconciled-row transfer subtitle.
 */
export const LineTransferSchema = z.object({
    id: z.string().uuid(),
    status: BankTransferStatusSchema,
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    date: z.string(),
    thisLineSide: z.enum(['source', 'dest']),
    counterpartBankAccountId: z.string().uuid(),
    counterpartBankAccountLabel: z.string(),
});
export const LineTransferResponseSchema = z.object({
    transfer: LineTransferSchema.nullable(),
});
